// =============================================================================
//  Copyright : 2023 semantics GmbH
//  Date      : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 159808 $
//  Purpose   : showcase components
// =============================================================================
// ---Third-party imports
import React from 'react'
import clsx from 'clsx'

// ---Framework imports
import log from 'uix/logging'
import {useApi, useSelector, useState, useCallback, useRef, useEffect, useInView} from 'uix/hooks'
import {Sel} from 'uix/registries'
import {Button} from 'uix/components'
import {GhostImage, GhostText, PageViewer, Modal} from '.'
import {VIcon} from 'uix/graphics'

// ---Local imports
import S from './showcase.scss'
import {useCaptionLink} from '../hooks'
import {XPopup, AccessWarning} from 'uix/components'
import {PopupContent, PopupHeader} from 'semantic-ui-react'

/// ---Exports
const useImageInfo = id => {
  let w = 1000
  let imgsrc = `/download/webcache/${w}/${id}`
  let [isError, setIsError] = useState(false)

  let onError = useCallback(() => setIsError(true), [])

  return {imgsrc, onError, isError}
}

export const Showcase = ({
  otid,
  otids,
  caption,
  fontSize,
  linkid,
  carousel,
  imagePosition,
  imageWidth = '504px',
  limitText,
  showFulltext,
  optionSet,
  skipFirstPage,
}) => {
  let apiurl = useSelector(Sel.apiurl)
  let [outerRef, inView] = useInView({triggerOnce: true, rootMargin: '100%'})
  let imgRef = useRef(null)
  let contentRef = useRef(null)
  let [hidden, setHidden] = useState(false)
  let [txtOverflow, setTxtOverflow] = useState(false)
  let [imgHeight, setImgHeight] = useState(false)
  let [imgLoad, setImgLoad] = useState(false)
  let [isFirstLoad, setIsFirstLoad] = useState(true)
  let [currentIndex, setCurrentIndex] = useState(0)
  let [centeredOtid, setCenteredOtid] = useState(undefined) // centered Otid in Modal
  let [modalOpen, setModalOpen] = useState(false)
  let [popupOpen, setPopupOpen] = useState(false)

  useEffect(() => {
    let imgHeight = imgRef.current?.height || 0
    let contentHeight = contentRef.current?.clientHeight || 0
    setHidden(false)
    setImgHeight(imgHeight)
    setTxtOverflow(imgHeight < contentHeight - 5)
  }, [imgLoad, currentIndex])

  let visible = !!otid && inView
  let {data, loading: apiLoading} = useApi(
    'presentation/getShowcaseData',
    {otid, resolveChildren: !!carousel},
    {condition: visible}
  )
  let loading = !data || (apiLoading && isFirstLoad)

  const flattenPages = (node, parentText = '', parentTitle = '') => {
    // bring children and pages on one level
    let currentLevel = []

    if (node.pages) {
      currentLevel.push(
        ...node.pages.map(p => ({
          ...p,
          text: p.text || node.text || parentText,
          title: p.title || node.title || parentTitle,
        }))
      )
    }

    if (node.children) {
      currentLevel.push(
        ...node.children.map(c => ({
          ...c,
          text: c.text || node.text || parentText,
          title: c.title || node.title || parentTitle,
          _isNode: true, // Markierung für Rekursion
        }))
      )
    }
    // sort on first level
    currentLevel.sort((a, b) => (a.otsort || 0) - (b.otsort || 0))

    // recursion on children (puts parent item.text & .info into children)
    let flattened = []
    currentLevel.forEach(item => {
      if (item._isNode) {
        flattened = flattened.concat(flattenPages(item, item.text, item.title))
      } else {
        flattened.push(item)
      }
    })

    return flattened
  }

  let allPages = carousel
    ? data
      ? flattenPages(data)
      : []
    : data?.pages?.[0]
    ? [{...data.pages[0], text: data.text, awstate: data.awstate}]
    : []

  let totalSlides = allPages.length
  let {imgsrc, onError} = useImageInfo(allPages[currentIndex]?.otid)

  let onGraphicClick = () => setModalOpen(true)
  let nextSlide = () => setCurrentIndex(prev => (prev + 1) % totalSlides)
  let prevSlide = () => setCurrentIndex(prev => (prev - 1 + totalSlides) % totalSlides)

  let img = (
    <AccessWarning id={currentIndex} awstate={allPages[currentIndex]?.awstate}>
      <img
        onLoad={() => {
          setImgLoad(true)
          setIsFirstLoad(false)
        }}
        onClick={onGraphicClick}
        src={apiurl.href(imgsrc)}
        onError={onError}
        ref={imgRef}
        loading="lazy"
      />
    </AccessWarning>
  )

  let textStyle = null
  if (!hidden && !loading) {
    textStyle = {
      maxHeight: !limitText ? 'none' : `${imgHeight - 5}px`,
      opacity: imgHeight ? 1 : 0,
      transition: 'opacity 0.3s ease-in-out',
    }
  }

  let buildMore = () => {
    if (!limitText) return null
    let handleMore = () => setHidden(!hidden)
    if (!hidden) {
      return (
        <a onClick={handleMore} className={S.more}>
          mehr <VIcon size="small" name="arrow down" />
        </a>
      )
    }
  }

  let getIdxByOtid = otid => {
    const idx = allPages.findIndex(p => p.otid === otid)
    if (idx !== -1) return idx
  }

  let modalClose = () => {
    setCurrentIndex(getIdxByOtid(centeredOtid))
    setModalOpen(false)
  }
  let centeredInfo = null
  if (centeredOtid) {
    centeredInfo = allPages.find(p => p.otid === centeredOtid)
  }

  const addInfoPopup = (
    <XPopup
      basic
      style={{opacity: 0.86, maxWidth: '66%', marginBottom: '2em'}}
      wide="very"
      inverted
      size="small"
      open={popupOpen}
      // onOpen={() => setPopupOpen(true)}
      // positionFixed
      position="right center"
      trigger={
        <Button
          style={{backgroundColor: 'var(--presentation_themeColor)', color: 'black'}}
          inverted
          onClick={() => setPopupOpen(!popupOpen)}
          icon={popupOpen ? 'close' : 'info'}
        />
      }
    >
      <PopupHeader>
        {centeredInfo?.title}{' '}
        {
          <VIcon
            style={{display: 'inline-block'}}
            name="link"
            href={apiurl.baseUrl('/' + centeredOtid)}
            target="_blank"
          />
        }
      </PopupHeader>
      <PopupContent>{centeredInfo?.text}</PopupContent>
    </XPopup>
  )

  let options = optionSet || (showFulltext && 'h1') || 'grid'

  let modalContent = (
    <PageViewer
      otids={allPages.map(pages => pages.otid)}
      otid={[allPages[currentIndex]?.otid]}
      onCenterChange={otid => setCenteredOtid(otid)}
      mobile={false}
      optionSet={options}
      navigationControls
      height="100%"
      zoomControls="inOut"
      initialZoomMode="areaFit"
      skipFirstPage={skipFirstPage}
      showFulltext={showFulltext}
      addContent={area => {
        if (!centeredInfo) return null
        if (area === 'viewer') {
          return (
            <span
              style={{
                color: 'red',
                position: 'absolute',
                bottom: '.3rem',
                left: '.3rem',
                zIndex: 20,
              }}
            >
              {addInfoPopup}
            </span>
          )
        }
      }}
    />
  )
  let captionLink = useCaptionLink(caption, linkid, otid, null, true)
  return (
    <div className={clsx(S.showcase, imagePosition === 'right' && S.imageRight)} ref={outerRef}>
      <div className={clsx(S.content, loading && S.loading)}>
        {carousel && (
          <div className={clsx(S.carouselNav, totalSlides > 1 && S.withSlides)}>
            <a
              className={clsx(S.prev, currentIndex <= 0 && S.disabled)}
              onClick={
                currentIndex > 0
                  ? () => {
                      setImgLoad(false)
                      setCurrentIndex(0)
                    }
                  : undefined
              }
            >
              <VIcon name="chevron_left_end" />
            </a>
            <a
              className={clsx(S.prev, currentIndex <= 0 && S.disabled)}
              onClick={currentIndex > 0 ? prevSlide : undefined}
            >
              <VIcon name="chevron_left" />
            </a>
            <span>
              {currentIndex + 1} / {totalSlides}
            </span>
            <a
              className={clsx(S.next, currentIndex >= totalSlides - 1 && S.disabled)}
              onClick={currentIndex < totalSlides - 1 ? nextSlide : undefined}
            >
              <VIcon name="chevron_right" />
            </a>
            <a
              className={clsx(S.next, currentIndex >= totalSlides - 1 && S.disabled)}
              onClick={
                currentIndex < totalSlides - 1
                  ? () => {
                      setImgLoad(false)
                      setCurrentIndex(totalSlides - 1)
                    }
                  : undefined
              }
            >
              <VIcon name="chevron_right_end" />
            </a>
          </div>
        )}
        <div
          className={clsx(S.imgContainer)}
          style={{'--presenation_showcase_imageWidth': imageWidth}}
        >
          {loading && <GhostImage width="100%" height={imgHeight || '320px'} />}
          <div style={{visibility: loading ? 'hidden' : 'visible'}}>
            {allPages[currentIndex]?.otid && (
              <>
                <div className={S.clickable}></div>
                <Button
                  size="large"
                  style={{
                    color: 'var(--presentation_themeColor)',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  }}
                  onClick={onGraphicClick}
                  className={S.zoomButton}
                  icon={'zoom'}
                  circle
                  inverted
                />
                {/* {showFulltext && (
                <Button
                  // size="large"
                  style={{
                    backgroundColor: 'var(--presentation_themeColor)',
                    color: 'rgba(0, 0, 0, 0.7)',
                  }}
                  onClick={onGraphicClick}
                  className={S.showFulltext}
                  icon={'transcript'}
                  circle
                  // inverted
                />
              )} */}
                {img}
                {captionLink}
              </>
            )}
          </div>
        </div>
        {loading ? (
          <GhostText style={{marginLeft: '20px', width: 'inherit'}} />
        ) : (
          <div
            style={textStyle}
            className={clsx(S.textArea, limitText && txtOverflow && !hidden && S.hidden)}
          >
            <div key={currentIndex} ref={contentRef} style={{fontSize: fontSize}}>
              {/* {allPages[currentIndex]?.title}*/}
              {allPages[currentIndex]?.text.split('\n').map((line, index) => (
                <div key={index} style={{minHeight: '1em'}}>
                  {line}
                </div>
              ))}
            </div>
          </div>
        )}
        {!loading && <div className={S.actions}>{txtOverflow && buildMore()}</div>}
      </div>
      {modalOpen && (
        <Modal
          mobile={false}
          open={modalOpen}
          onClose={modalClose}
          imageId={allPages[currentIndex]?.otid}
        >
          {modalContent}
        </Modal>
      )}
    </div>
  )
}
